import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { openWhatsApp, WhatsAppIcon } from "@/components/Cta";

const NAV = [
  { to: "/", label: "首页" },
  { to: "/services", label: "我们的服务" },
  { to: "/#stages", label: "解决方案" },
  { to: "/about", label: "关于我们" },
  { to: "/blog", label: "电商资讯" },
  { to: "/faq", label: "常见问题" },
];

export function Logo({ light = false }) {
  return (
    <Link to="/" className="flex items-baseline gap-0.5" data-testid="site-logo">
      <span className={`font-display text-2xl font-semibold tracking-tight ${light ? "text-paper" : "text-ink"}`}>
        TKShop
      </span>
      <span className="font-display text-2xl font-semibold italic text-moss">MY</span>
      <span className="ml-0.5 h-2 w-2 rounded-full bg-lime" />
    </Link>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow] duration-300 ${
          scrolled ? "bg-paper/90 shadow-[0_1px_0_rgba(16,23,17,0.08)] backdrop-blur-md" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 md:h-20 md:px-8">
          <Logo />
          <nav className="hidden items-center gap-7 lg:flex" aria-label="Main navigation">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors hover:text-forest ${
                    isActive && !n.to.includes("#") ? "text-forest" : "text-ink/60"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={() => openWhatsApp("header", { text: "WhatsApp 咨询" })}
              className="group hidden items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-paper transition-colors hover:bg-ink sm:flex"
              data-testid="header-chat-button"
              data-location="header"
            >
              <WhatsAppIcon size={15} className="transition-transform group-hover:-rotate-12" />
              WhatsApp 咨询
            </button>
            <button
              className="rounded-full border border-ink/15 p-2.5 lg:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-button"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 bg-forest text-paper"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <Logo light />
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="rounded-full border border-paper/25 p-2.5" data-testid="mobile-menu-close">
                <X size={18} />
              </button>
            </div>
            <nav className="flex flex-col gap-1 px-5 pt-8" aria-label="Mobile navigation">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    className="block border-b border-paper/10 py-4 font-display text-3xl font-medium"
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                onClick={() => {
                  setOpen(false);
                  openWhatsApp("mobile-menu", { text: "WhatsApp 咨询" });
                }}
                className="mt-8 flex items-center justify-center gap-2 rounded-full bg-lime py-4 text-base font-semibold text-ink"
                data-testid="mobile-menu-chat-button"
                data-location="mobile-menu"
              >
                <WhatsAppIcon size={18} />
                WhatsApp 咨询
              </motion.button>
              <p className="mt-4 text-center text-xs text-paper/50">可以先问清楚，不需要填表格。</p>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
